import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Dropdown, Form, Grid, Input, Message } from 'semantic-ui-react';
import { CreateProgrammingRequest, EProgrammingTravelType, Programming, UpdateProgrammingRequest } from '../../../../models/programming';
import { ShortDto } from '../../../../models/short-dto';
import { resolveIsGoAndReturn, toDatetimeLocalValue } from '../../../../utils/programming-timeline-utils';

interface ProgrammingFormProps {
    initialData?: Programming;
    isEdit?: boolean;
    onSubmit: (data: CreateProgrammingRequest | UpdateProgrammingRequest) => void;
    onCancel: () => void;
    loading: boolean;
    error: string | undefined;
    clients: ShortDto[];
    vehiclePlates: ShortDto[];
    products: ShortDto[];
    drivers: ShortDto[];
    references: ShortDto[];
    routes: ShortDto[];
    lockedClientId?: string;
    clientDisabled?: boolean;
    /** Labels/help alinhados à operação da planilha TNE (só Torre). */
    variant?: 'default' | 'torre';
}

type ProgrammingFormData = CreateProgrammingRequest | UpdateProgrammingRequest;

const ProgrammingForm: React.FC<ProgrammingFormProps> = ({
    initialData,
    isEdit = false,
    onSubmit,
    onCancel,
    loading,
    error,
    clients,
    vehiclePlates,
    products,
    drivers,
    references,
    routes,
    lockedClientId,
    clientDisabled = false,
    variant = 'default',
}) => {
    const isTorre = variant === 'torre';
    const labels = {
        plateCm: isTorre ? 'Placa (cavalo)' : 'Placa CM',
        travelType: isTorre ? 'Tipo de viagem' : 'Tipo de Deslocamento',
        originDeparture: 'Saída prevista — origem',
        destinationArrival: 'Chegada prevista — destino',
    };
    const [formData, setFormData] = useState<ProgrammingFormData>({
        clientId: lockedClientId || '',
        volume: 0,
        productId: '',
        travelType: EProgrammingTravelType.Empty,
        originReferenceId: '',
        destinationReferenceId: '',
        returnDestinationReferenceId: '',
        isGoAndReturn: false,
    });

    useEffect(() => {
        if (lockedClientId) {
            setFormData(prev => ({ ...prev, clientId: lockedClientId }));
        }
    }, [lockedClientId]);

    useEffect(() => {
        if (initialData) {
            const isGoAndReturn = resolveIsGoAndReturn(initialData);
            setFormData({
                clientId: initialData.clientId,
                mainVehiclePlateId: initialData.mainVehiclePlateId,
                semiTrailer1Id: initialData.semiTrailer1Id,
                semiTrailer2Id: initialData.semiTrailer2Id,
                volume: initialData.volume,
                productId: initialData.productId,
                driverId: initialData.driverId,
                travelType: initialData.travelType,
                routeId: initialData.routeId,
                originReferenceId: initialData.originReferenceId,
                destinationReferenceId: initialData.destinationReferenceId,
                expectedOriginDepartureAt: toDatetimeLocalValue(
                    initialData.expectedOriginDepartureAt ?? initialData.expectedDepartureTime
                ),
                expectedDestinationArrivalAt: toDatetimeLocalValue(
                    initialData.expectedDestinationArrivalAt ?? initialData.expectedArrivalTime
                ),
                expectedDestinationDepartureAt: toDatetimeLocalValue(initialData.expectedDestinationDepartureAt),
                expectedReturnArrivalAt: toDatetimeLocalValue(initialData.expectedReturnArrivalAt),
                distanceKm: initialData.distanceKm,
                isGoAndReturn,
                isReturn: isGoAndReturn,
                returnDestinationReferenceId: initialData.returnDestinationReferenceId ?? '',
            });
        }
    }, [initialData]);

    const handleChange = (field: string, value: unknown) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSubmit = () => {
        const isGoAndReturn = formData.isGoAndReturn ?? formData.isReturn ?? false;
        onSubmit({
            ...formData,
            isGoAndReturn,
            isReturn: isGoAndReturn,
            expectedDepartureTime: formData.expectedOriginDepartureAt,
            expectedArrivalTime: formData.expectedDestinationArrivalAt,
        });
    };

    const createOptions = (items: ShortDto[]) => {
        return items.map(item => ({
            key: item.key,
            text: item.text,
            value: item.value,
        }));
    };

    const travelTypeOptions = [
        { key: EProgrammingTravelType.Empty, text: 'Vazio', value: EProgrammingTravelType.Empty },
        { key: EProgrammingTravelType.Loaded, text: 'Carregado', value: EProgrammingTravelType.Loaded },
    ];

    const clientOptions = createOptions(clients);
    const vehiclePlateOptions = createOptions(vehiclePlates);
    const productOptions = createOptions(products);
    const driverOptions = createOptions(drivers);
    const referenceOptions = createOptions(references);
    const isGoAndReturn = formData.isGoAndReturn ?? formData.isReturn ?? false;

    return (
        <Form loading={loading} error={!!error}>
            {error && <Message error content={error} />}
            {isTorre && (
                <Message
                    info
                    data-testid="torre-form-help"
                    content="Cadastro alinhado à operação da planilha de disponibilidade (programação diária). Preencha placa, origem/destino e horários previstos como na planilha."
                />
            )}

            <Form.Group widths="equal">
                <Form.Field required>
                    <label>Cliente</label>
                    <Dropdown
                        placeholder="Selecione o cliente"
                        fluid
                        search
                        selection
                        disabled={clientDisabled || !!lockedClientId}
                        options={clientOptions}
                        value={formData.clientId || lockedClientId || ''}
                        onChange={(_, { value }) => handleChange('clientId', value)}
                    />
                </Form.Field>
                <Form.Field>
                    <label>{labels.plateCm}</label>
                    <Dropdown
                        placeholder="Selecione a placa principal"
                        fluid
                        search
                        selection
                        options={vehiclePlateOptions}
                        value={formData.mainVehiclePlateId || ''}
                        onChange={(_, { value }) => handleChange('mainVehiclePlateId', value)}
                    />
                </Form.Field>
            </Form.Group>
            
            <Form.Group widths="equal">
                <Form.Field>
                    <label>Placa SR1</label>
                    <Dropdown
                        placeholder="Selecione a placa SR1"
                        fluid
                        search
                        selection
                        clearable
                        options={vehiclePlateOptions}
                        value={formData.semiTrailer1Id || ''}
                        onChange={(_, { value }) => handleChange('semiTrailer1Id', value)}
                    />
                </Form.Field>
                <Form.Field>
                    <label>Placa SR2</label>
                    <Dropdown
                        placeholder="Selecione a placa SR2"
                        fluid
                        search
                        selection
                        clearable
                        options={vehiclePlateOptions}
                        value={formData.semiTrailer2Id || ''}
                        onChange={(_, { value }) => handleChange('semiTrailer2Id', value)}
                    />
                </Form.Field>
            </Form.Group>
            
            <Form.Group widths="equal">
                <Form.Field required>
                    <label>Volume</label>
                    <Input
                        placeholder="Volume"
                        type="number"
                        step="0.001"
                        value={formData.volume || ''}
                        onChange={(e) => handleChange('volume', parseFloat(e.target.value))}
                    />
                </Form.Field>
                <Form.Field required>
                    <label>Produto</label>
                    <Dropdown
                        placeholder="Selecione o produto"
                        fluid
                        search
                        selection
                        options={productOptions}
                        value={formData.productId || ''}
                        onChange={(_, { value }) => handleChange('productId', value)}
                    />
                </Form.Field>
            </Form.Group>
            
            <Form.Group widths="equal">
                <Form.Field>
                    <label>Motorista</label>
                    <Dropdown
                        placeholder="Selecione o motorista"
                        fluid
                        search
                        selection
                        clearable
                        options={driverOptions}
                        value={formData.driverId || ''}
                        onChange={(_, { value }) => handleChange('driverId', value)}
                    />
                </Form.Field>
                <Form.Field required>
                    <label>{labels.travelType}</label>
                    <Dropdown
                        placeholder="Selecione o tipo"
                        fluid
                        selection
                        options={travelTypeOptions}
                        value={formData.travelType}
                        onChange={(_, { value }) => handleChange('travelType', value)}
                    />
                </Form.Field>
            </Form.Group>
            
            <Form.Group widths="equal">
                <Form.Field required>
                    <label>Referência de Origem</label>
                    <Dropdown
                        placeholder="Selecione a origem"
                        fluid
                        search
                        selection
                        options={referenceOptions}
                        value={formData.originReferenceId || ''}
                        onChange={(_, { value }) => handleChange('originReferenceId', value)}
                    />
                </Form.Field>
                <Form.Field required>
                    <label>Referência de Destino</label>
                    <Dropdown
                        placeholder="Selecione o destino"
                        fluid
                        search
                        selection
                        options={referenceOptions}
                        value={formData.destinationReferenceId || ''}
                        onChange={(_, { value }) => handleChange('destinationReferenceId', value)}
                    />
                </Form.Field>
            </Form.Group>

            <Form.Group widths="equal">
                <Form.Field>
                    <Checkbox
                        label="Ida e volta (retorno diferente da origem)?"
                        checked={isGoAndReturn}
                        onChange={(_, { checked }) => {
                            setFormData(prev => ({
                                ...prev,
                                isGoAndReturn: checked,
                                isReturn: checked,
                                returnDestinationReferenceId: checked ? prev.returnDestinationReferenceId : '',
                                expectedReturnArrivalAt: checked ? prev.expectedReturnArrivalAt : undefined,
                            }));
                        }}
                    />
                </Form.Field>

                {isGoAndReturn && (
                    <Form.Field required>
                        <label>Referência de Retorno</label>
                        <Dropdown
                            placeholder="Selecione a referência de retorno"
                            fluid
                            search
                            selection
                            options={referenceOptions}
                            value={formData.returnDestinationReferenceId || ''}
                            onChange={(_, { value }) => handleChange('returnDestinationReferenceId', value)}
                        />
                    </Form.Field>
                )}
            </Form.Group>

            <Form.Group widths="equal">
                <Form.Field>
                    <label>{labels.originDeparture}</label>
                    <Input
                        type="datetime-local"
                        value={formData.expectedOriginDepartureAt || ''}
                        onChange={(e) => handleChange('expectedOriginDepartureAt', e.target.value || undefined)}
                    />
                </Form.Field>
                <Form.Field>
                    <label>{labels.destinationArrival}</label>
                    <Input
                        type="datetime-local"
                        value={formData.expectedDestinationArrivalAt || ''}
                        onChange={(e) => handleChange('expectedDestinationArrivalAt', e.target.value || undefined)}
                    />
                </Form.Field>
            </Form.Group>

            <Form.Group widths="equal">
                <Form.Field>
                    <label>Saída prevista — destino</label>
                    <Input
                        type="datetime-local"
                        value={formData.expectedDestinationDepartureAt || ''}
                        onChange={(e) => handleChange('expectedDestinationDepartureAt', e.target.value || undefined)}
                    />
                </Form.Field>
                {isGoAndReturn && (
                    <Form.Field>
                        <label>Chegada prevista — retorno</label>
                        <Input
                            type="datetime-local"
                            value={formData.expectedReturnArrivalAt || ''}
                            onChange={(e) => handleChange('expectedReturnArrivalAt', e.target.value || undefined)}
                        />
                    </Form.Field>
                )}
            </Form.Group>
            
            <Grid columns={2}>
                <Grid.Column textAlign="left">
                    <Button type="button" onClick={onCancel}>
                        Cancelar
                    </Button>
                </Grid.Column>
                <Grid.Column textAlign="right">
                    <Button
                        color={"green"}
                        type="submit" 
                        onClick={handleSubmit} 
                        disabled={loading || !formData.clientId || !formData.productId || !formData.volume || !formData.originReferenceId || !formData.destinationReferenceId}
                    >
                        {isEdit ? 'Atualizar' : 'Criar'}
                    </Button>
                </Grid.Column>
            </Grid>
        </Form>
    );
};

export default ProgrammingForm;
